import type { Address } from './api';

export type MapsPlatform = 'apple' | 'android' | 'other';

/** Which maps app the device has: Apple's on iPhone, iPad and Mac, the `geo:` handler on Android. */
export function mapsPlatform(userAgent: string): MapsPlatform {
  if (/android/i.test(userAgent)) return 'android';
  if (/iphone|ipad|ipod|macintosh/i.test(userAgent)) return 'apple';
  return 'other';
}

/** The address as one line: the text the person confirmed, else its parts. Null when empty. */
export function addressLine(address: Address | null | undefined): string | null {
  if (address === null || address === undefined) return null;
  const text = address.text?.trim();
  if (text) return text;
  const street = [address.street, address.streetNumber].filter(Boolean).join(' ');
  const town = [address.postalCode, address.city, address.province].filter(Boolean).join(' ');
  const line = [street, town].filter((part) => part !== '').join(', ');
  return line === '' ? null : line;
}

/**
 * The link that opens the device's maps app on the address (U16: no map inside the page). Apple
 * Maps on Apple devices, `geo:` on Android, Google Maps on the web otherwise, with Google's place
 * id when the address component kept one.
 */
export function mapsHref(address: Address, platform: MapsPlatform): string | null {
  const line = addressLine(address);
  if (line === null) return null;
  const query = encodeURIComponent(line);
  if (platform === 'apple') return `https://maps.apple.com/?q=${query}`;
  if (platform === 'android') return `geo:0,0?q=${query}`;
  const place = address.placeId ? `&query_place_id=${encodeURIComponent(address.placeId)}` : '';
  return `https://www.google.com/maps/search/?api=1&query=${query}${place}`;
}

/** A phone number as a `tel:` link: the digits and the leading +. */
export function telHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
}
